import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, X } from 'lucide-react';
import { searchLocations } from '../services/location.api';
import type { Location } from '../services/api.types';
import { getAccessToken } from '../services/auth';
import './LocationAutocomplete.css';
import './ThemedDropdown.css';

interface LocationAutocompleteProps {
  value: Location | null;
  onChange: (location: Location | null) => void;
  placeholder: string;
  ariaLabel: string;
  ariaDescribedBy?: string;
  required?: boolean;
  suggestions?: Location[];
  invalid?: boolean;
}

const label = (location: Location) =>
  `${location.diacriticsName || location.name}, ${location.county.name}`;

const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
  ariaDescribedBy,
  required,
  suggestions = [],
  invalid = false,
}) => {
  const [query, setQuery] = useState(value ? label(value) : '');
  const [options, setOptions] = useState<Location[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const requestRef = useRef(0);
  const visibleOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('ro');
    const matchingSuggestions = suggestions.filter((location) => (
      !normalizedQuery || label(location).toLocaleLowerCase('ro').includes(normalizedQuery)
    ));
    return [...matchingSuggestions, ...options].filter((location, index, all) => (
      all.findIndex((candidate) => candidate.id === location.id) === index
    ));
  }, [options, query, suggestions]);

  useEffect(() => {
    setQuery(value ? label(value) : '');
  }, [value]);

  useEffect(() => {
    const close = (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, []);

  useEffect(() => {
    const term = query.trim();
    if (value || term.length < 2) {
      setOptions([]);
      return;
    }
    const timeout = window.setTimeout(async () => {
      const requestId = ++requestRef.current;
      setLoading(true);
      try {
        const response = await searchLocations(1, 20, null, term, getAccessToken());
        if (requestId === requestRef.current) {
          setOptions(response.locations.content);
          setOpen(true);
        }
      } catch (error) {
        console.error('Could not search delivery locations', error);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [query, value]);

  return (
    <div className={`location-autocomplete ${invalid ? 'location-autocomplete-invalid' : ''}`} ref={ref}>
      <MapPin size={18} aria-hidden />
      <input
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          if (value) {
            onChange(null);
            setOpen(suggestions.length > 0 || options.length > 0);
          }
        }}
        onFocus={() => {
          if (!value && (suggestions.length > 0 || options.length > 0)) setOpen(true);
        }}
        placeholder={placeholder}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-invalid={invalid}
        required={required}
        autoComplete="off"
      />
      {(query || value) && (
        <button
          type="button"
          className="location-autocomplete-clear"
          onClick={() => {
            setQuery('');
            onChange(null);
            setOpen(suggestions.length > 0);
          }}
          aria-label="Șterge locația"
        >
          <X size={16} aria-hidden />
        </button>
      )}
      {open && (
        <div className="location-autocomplete-options themed-dropdown-menu">
          {loading && visibleOptions.length === 0 ? (
            <div className="location-autocomplete-state">Se caută...</div>
          ) : visibleOptions.length > 0 ? visibleOptions.map((location) => (
            <button
              type="button"
              className="location-autocomplete-option themed-dropdown-option"
              key={location.id}
              onClick={() => {
                onChange(location);
                setQuery(label(location));
                setOpen(false);
              }}
            >
              <strong>{location.diacriticsName || location.name}</strong>
              <span>{location.county.name}</span>
            </button>
          )) : (
            <div className="location-autocomplete-state">Nicio locație găsită</div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
